import { HttpClient, HttpHeaders } from '@angular/common/http';
import { KeycloakSecurityService } from '../../Keycloak-config/keycloak-security.service';
import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Organization } from '../../Models/organization/entities/organization.entity';
import { setupServicesDTO } from '../../Models/organization/setupServices-dto';
import { storeIpAddressesDTO } from '../../Models/organization/storeIpAddresses-dto';
import {BehaviorSubject} from "rxjs";
import { ServiceComponentDTO } from '../../Models/serviceComponent/serviceComponent-dto';

@Injectable({
  providedIn: 'root'
})
export class OrganizationsService {
  private apiUrl = environment.API_URL;

  private organizationsSubject = new BehaviorSubject<any[]>([]);
  organizations$ = this.organizationsSubject.asObservable();

  private selectedOrganizationIdSubject = new BehaviorSubject<string>(null);
  selectedOrganizationId$ = this.selectedOrganizationIdSubject.asObservable();

  private localStorageKey = 'selectedOrganizationId';

  constructor(private http: HttpClient, private keycloakSecurityService:KeycloakSecurityService) {
    const savedOrgId = this.readSavedOrganizationId();
    if (this.keycloakSecurityService.kc.tokenParsed.realm_access.roles.includes('admin')){
      // Sans ce garde, une selection absente ecrasait l'organisation issue du jeton
      // (cf. app.component.ts) et persistait la chaine "null" dans le stockage.
      if (savedOrgId) {
        this.setSelectedOrganizationId(savedOrgId);
      }
      this.getOrganizations().subscribe((response: any[]) => {
        this.updateOrganizationsState(response);

        // Select the saved organization if it exists and matches the retrieved list
        const matchedOrganization = response.find(org => org.organization_id === savedOrgId);

        if (!matchedOrganization && response.length > 0) {
          // Default to the first organization if no match is found
          this.setSelectedOrganizationId(response[0].organization_id);
        }
      });
    }
  }

  getOrganization(organization_id: string) {
    try {
      return this.http.get(`${this.apiUrl}/organization/${organization_id}`);
    } catch (error) {
      throw new Error(error);
    }
  }
  getOrganizations() {
    try {
      return this.http.get(`${this.apiUrl}/organization/`);
    } catch (error) {
      throw new Error(error);
    }
  }

  addOrganization(organization: Organization) {
    return this.http.post(this.apiUrl+'/organization/',
    organization,
    );
  }
  updateOrganization(organization: Organization) {
    return this.http.patch(this.apiUrl+`/organization/${organization.organization_id}`,
    organization,
    );
  }

  deleteOrganization(organization_id: string) {
    try {
      return this.http.delete(this.apiUrl + `/organization/${organization_id}`)
    } catch (error) {
      throw new Error(error);
    }
  }

  checkK8sAuthfileCreated(organization_id : string){
    return this.http.get(this.apiUrl+`/organization/${organization_id}/checkK8sAuthFile`);
  }

  uploadKubeConfigFile(organization_id: string, file: File){
    const formData = new FormData();
    formData.set("file", file, file.name);
    try {
      return this.http.post(this.apiUrl+'/organization/'+organization_id+'/uploadKubeConfigFile',
        formData,
      );
    } catch (error) {
      throw new Error(error);
    }
  }

  generateLoadBalancerFiles(organization_id: string){
    try {
      return this.http.post(this.apiUrl+'/organization/'+organization_id+'/generateLoadBalancerFiles',
        {},
        { responseType: 'text' }
      );
    } catch (error) {
      throw new Error(error);
    }
  }

  storeIpAddresses(organization_id: string, storeIpAddressesDto : storeIpAddressesDTO){
    try {
      return this.http.post(this.apiUrl+'/organization/'+organization_id+'/storeIpAddresses',
        storeIpAddressesDto
      );
    } catch (error) {
      throw new Error(error);
    }
  }

  checkDnsConfiguration(organization_id: string){
    try {
      return this.http.post(this.apiUrl+'/organization/'+organization_id+'/checkDnsConfiguration',
        {}
      );
    } catch (error) {
      throw new Error(error);
    }
  }

  setupFrontalService(organization_id: string, setupServicesDto: setupServicesDTO){
    try {
      return this.http.post(this.apiUrl+'/organization/'+organization_id+'/setupFrontalService',
        setupServicesDto
      );
    } catch (error) {
      throw new Error(error);
    }
  }

  ValidateAutoSignedCertsStep(organization_id: string){
    try {
      return this.http.post(this.apiUrl+'/organization/'+organization_id+'/ValidateAutoSignedCertsStep',
        {}
      );
    } catch (error) {
      throw new Error(error);
    }
  }
  
  setupServices(organization_id: string, setupServicesDto: setupServicesDTO){
    try {
      return this.http.post(this.apiUrl+'/organization/'+organization_id+'/setupServices',
        setupServicesDto
      );
    } catch (error) {
      throw new Error(error);
    }
  }

  markOrgaizationCreationAsDone(organization_id: string){
    try {
      return this.http.post(this.apiUrl+'/organization/'+organization_id+'/markOrgaizationCreationAsDone',{});
    } catch (error) {
      throw new Error(error);
    }
  } 

  ValidateDkimCertsStep(organization_id: string){
    try {
      return this.http.post(this.apiUrl+'/organization/'+organization_id+'/ValidateDkimCertsStep',{});
    } catch (error) {
      throw new Error(error);
    }
  }

  updateOrganizationsState(organizations: any[]): void {
    this.organizationsSubject.next(organizations);
  }

  // Manage selected organization
  setSelectedOrganizationId(organizationId: string): void {
    this.selectedOrganizationIdSubject.next(organizationId);
    if (!organizationId) {
      return;
    }
    this.writeSavedOrganizationId(organizationId);

    environment.ORGANIZATION_ID = organizationId; // Update the environment value
  }

  /**
   * La selection est propre a l'onglet : sessionStorage fait foi. localStorage n'est
   * plus qu'une graine pour un onglet neuf, afin de retrouver la derniere organisation
   * utilisee. Deux onglets ouverts sur deux organisations differentes ne se reecrivent
   * donc plus mutuellement au rechargement.
   */
  private readSavedOrganizationId(): string {
    try {
      const saved = sessionStorage.getItem(this.localStorageKey)
        || localStorage.getItem(this.localStorageKey);
      // Les versions precedentes pouvaient persister la chaine "null".
      return saved && saved !== 'null' && saved !== 'undefined' ? saved : null;
    } catch (e) {
      // Stockage indisponible : on repart sans selection memorisee.
      return null;
    }
  }

  private writeSavedOrganizationId(organizationId: string): void {
    try {
      sessionStorage.setItem(this.localStorageKey, organizationId);
      localStorage.setItem(this.localStorageKey, organizationId);
    } catch (e) {
      // Stockage indisponible : la selection reste valable pour la session en cours.
    }
  }

  getSelectedOrganizationId(): string {
    return this.selectedOrganizationIdSubject.getValue();
  }

  getSmsCredits() {
    try {
      return this.http.get(`${this.apiUrl}/organization/credits`);
    } catch (error) {
      throw new Error(error);
    }
  }

  getConfigurationServices() {
    try {
      return this.http.get(`${this.apiUrl}/organization/components`);
    } catch (error) {
      throw new Error(error);
    }
  }
  
  updateConfigurationServices(serviceComponentDto: ServiceComponentDTO) {
    try {
      return this.http.post(this.apiUrl+`/organization/components/update`,
        serviceComponentDto
      );
    } catch (error) {
      throw new Error(error);
    }
  }
}
